import { useEffect } from "react";
import { WebRTCConnection } from "../models/WebRTCConnection";

interface UseWindowFocusProps {
  connection?: WebRTCConnection;
  audioConnection?: WebRTCConnection;
  onError?: (error: Error) => void;
  pauseAudio?: () => void;
  resumeAudioStream?: () => Promise<void>;
  isWebos?: boolean;
  isTV?: boolean;
}

/**
 * A hook to handle window focus and blur events for WebRTC streaming
 * - Resets keyboard state on focus/blur to avoid stuck keys
 * - Syncs clipboard content when window gains focus
 * - Pauses/resumes audio when window or app state changes
 * - Disables the streamed tracks while a TV application is backgrounded
 */
export const useWindowFocus = ({
  isWebos,
  isTV,
  connection,
  audioConnection,
  pauseAudio,
  resumeAudioStream,
}: UseWindowFocusProps) => {
  useEffect(() => {
    if (!connection) return;

    const resetKeyboard = () => {
      try {
        connection.sendDataChannelMessage("kr");
      } catch (error) {
        console.warn("Failed to reset keyboard state:", error);
      }
    };

    const syncClipboard = async () => {
      try {
        const text = await navigator.clipboard.readText();
        const bytes = new TextEncoder().encode(text);
        const base64Text = btoa(
          Array.from(bytes, (byte) => String.fromCodePoint(byte)).join(""),
        );
        connection.sendDataChannelMessage(`cw,${base64Text}`);
      } catch (error) {
        console.warn("Failed to sync clipboard content:", error);
      }
    };

    const handleFocus = () => {
      resetKeyboard();
      syncClipboard();
    };

    const handleBlur = () => {
      resetKeyboard();
    };

    // Samsung TV game mode requires every track to be disabled in the
    // background and re-enabled in the foreground. Browsers keep streaming so
    // their existing multitasking behaviour is left alone.
    const setTracksEnabled = (enabled: boolean) => {
      if (!isTV) return;
      connection.setTracksEnabled(enabled);
      audioConnection?.setTracksEnabled(enabled);
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (isWebos) {
          window?.close();
          return;
        }

        setTracksEnabled(false);
        pauseAudio?.();
        handleBlur();
      } else {
        setTracksEnabled(true);
        resumeAudioStream?.();
        handleFocus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    connection,
    audioConnection,
    pauseAudio,
    resumeAudioStream,
    isWebos,
    isTV,
  ]);
};
